
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Save, RefreshCw, Settings, AlertTriangle, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";

interface AgentOverride {
  id: string;
  agent: string;
  metric: string;
  originalValue: string | number;
  overrideValue: string | number;
  enabled: boolean;
  lastModified: Date;
}

const mockOverrides: AgentOverride[] = [
  {
    id: "1",
    agent: "Poniente",
    metric: "Cash Flow Forecast",
    originalValue: 125000,
    overrideValue: 150000,
    enabled: true,
    lastModified: new Date()
  },
  {
    id: "2", 
    agent: "Full Send",
    metric: "Quote Conversion Rate",
    originalValue: "75%",
    overrideValue: "85%",
    enabled: false,
    lastModified: new Date()
  },
  {
    id: "3",
    agent: "Levante",
    metric: "Credit Risk Score",
    originalValue: 720,
    overrideValue: 750,
    enabled: true,
    lastModified: new Date()
  }
];

// Mock permission check - in real app this would come from auth
const hasPermission = (permission: string): boolean => {
  // Simulate checking user permissions
  const userRole = "CEO"; // This would come from actual auth context
  const rolePermissions = {
    "CEO": ["approve_agent_overrides", "edit_forecasts", "manage_payment_terms"],
    "CFO": ["approve_agent_overrides", "edit_forecasts"],
    "Controller": ["approve_agent_overrides"],
    "Analyst": []
  };
  return rolePermissions[userRole]?.includes(permission) || false;
};

export default function RefineWindroseMetrics() {
  const [overrides, setOverrides] = useState<AgentOverride[]>(mockOverrides);
  const [simulationEnabled, setSimulationEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const canApproveOverrides = hasPermission("approve_agent_overrides");

  if (!canApproveOverrides) {
    return (
      <div className="flex items-center justify-center p-8 text-center">
        <div className="space-y-4">
          <Lock className="h-12 w-12 text-muted-foreground mx-auto" />
          <div>
            <h3 className="text-lg font-semibold">{t('dashboard.accessRestricted', 'Access Restricted')}</h3>
            <p className="text-muted-foreground">
              {t('dashboard.noPermission', "You don't have permission to access Windrose Metrics refinement. Please contact your administrator.")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleSaveOverrides = async () => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: t('dashboard.windroseSaved', 'Windrose Metrics Updated'),
        description: t('dashboard.saveSuccess', 'Agent metric refinements have been saved successfully.')
      });
    } catch (error) {
      toast({
        title: t('common.error', 'Error'),
        description: t('dashboard.saveFailed', 'Failed to save metric refinements. Please try again.'),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleOverride = (id: string) => {
    setOverrides(prev => prev.map(override => 
      override.id === id 
        ? { ...override, enabled: !override.enabled }
        : override
    ));
  };

  const handleValueChange = (id: string, newValue: string | number) => {
    setOverrides(prev => prev.map(override => 
      override.id === id 
        ? { ...override, overrideValue: newValue, lastModified: new Date() }
        : override
    ));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4" />
          <span className="text-sm font-medium">{t('dashboard.simulationMode', 'Windrose Simulation Mode')}</span>
        </div>
        <Switch 
          checked={simulationEnabled} 
          onCheckedChange={setSimulationEnabled}
        />
      </div>

      {simulationEnabled && (
        <div className="flex items-center gap-2 p-3 bg-warning-muted border border-warning/30 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <span className="text-sm text-warning">
            {t('dashboard.simulationActive', 'Simulation mode active - Windrose outputs will use refined values')}
          </span>
        </div>
      )}

      <Tabs defaultValue="overrides" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="overrides">{t('dashboard.activeRefinements', 'Active Refinements')}</TabsTrigger>
          <TabsTrigger value="settings">{t('nav.settings', 'Settings')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overrides" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{t('dashboard.metricRefinements', 'Windrose Metric Refinements')}</span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.reload()}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    {t('dashboard.reset', 'Reset')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveOverrides}
                    disabled={isLoading}
                  >
                    <Save className="h-3 w-3 mr-1" />
                    {isLoading ? t('dashboard.saving', 'Saving...') : t('dashboard.save', 'Save')}
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('dashboard.tableAgent', 'Agent')}</TableHead>
                    <TableHead>{t('dashboard.tableMetric', 'Metric')}</TableHead>
                    <TableHead>{t('dashboard.tableOriginal', 'Original')}</TableHead>
                    <TableHead>{t('dashboard.tableRefined', 'Refined Value')}</TableHead>
                    <TableHead>{t('dashboard.tableEnabled', 'Enabled')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overrides.map((override) => (
                    <TableRow key={override.id}>
                      <TableCell>
                        <Badge variant="outline">{override.agent}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {override.metric}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {override.originalValue}
                      </TableCell>
                      <TableCell>
                        <Input
                          value={override.overrideValue}
                          onChange={(e) => handleValueChange(override.id, e.target.value)}
                          className="w-24"
                          disabled={!override.enabled}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={override.enabled}
                          onCheckedChange={() => handleToggleOverride(override.id)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('dashboard.refinementSettings', 'Refinement Settings')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="scope">{t('dashboard.permissionScope', 'Permission Scope')}</Label>
                <Input 
                  id="scope" 
                  value="windrose_metric_refinement" 
                  readOnly 
                  className="bg-muted"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="backup">{t('dashboard.autoBackup', 'Auto-backup Frequency')}</Label>
                <select className="w-full p-2 border border-input rounded-md">
                  <option value="hourly">{t('dashboard.everyHour', 'Every Hour')}</option>
                  <option value="daily">{t('dashboard.daily', 'Daily')}</option>
                  <option value="weekly">{t('dashboard.weekly', 'Weekly')}</option>
                </select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
